// src/pages/CountriesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import styles from './CountriesPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import apiClient from '../services/ApiClient';
import CountryEditModal from '../components/layout/CountryEditModal';
import { toast } from 'react-toastify';
import { readApiError } from '../services/catalogApi';

// Hangi dil alanlarının tabloda gösterileceğini tanımlayalım
const displayLanguageFields = [
    { key: 'country', label: 'Varsayılan Ad' },
    { key: 'countryTR', label: 'Türkçe Adı' },
    { key: 'countryDE', label: 'Almanca Adı' },
    // { key: 'countryEN', label: 'İngilizce Adı' },
];

// API'den gelen /countries-data-light verisini bizim kullandığımız formata dönüştürmek için
// Tüm dil alanlarını dahil ediyoruz
const transformApiCountryData = (apiDataArray) => {
    if (!apiDataArray || !Array.isArray(apiDataArray)) {
        console.error("transformApiCountryData: Beklenen formatta (dizi) veri gelmedi.", apiDataArray);
        return [];
    }
    
    return apiDataArray.map((country, index) => ({
        // API'nızın döndürdüğü ID alanını buraya yazın.
        // Eğer ID yoksa ve ülke adı benzersizse, onu kullanabilirsiniz.
        id: country.id || country.country || `country-${index}`, // API'nizden gelen ID'yi kullanın
        country: country.country,
        countryDE: country.countryDE,
        countryTR: country.countryTR,
        countryFR: country.countryFR,
        countryES: country.countryES,
        countryAR: country.countryAR,
        countryPT: country.countryPT,
        countryRU: country.countryRU,
        countryUR: country.countryUR,
        countryIT: country.countryIT,
        countrySR: country.countrySR,
        countryID: country.countryID,
        countryMK: country.countryMK,
        countryHU: country.countryHU,
        countryJA: country.countryJA,
        countryZH: country.countryZH,
        countryTH: country.countryTH,
        flag: country.flag,
        isActive: country.isActive === null || country.isActive === undefined ? true : country.isActive,
    }));
};


const CountriesPage = () => {
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState('');
  const [savingStates, setSavingStates] = useState({}); // { countryId: true } — satır kaydediliyor

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCountryForEdit, setSelectedCountryForEdit] = useState(null);

  // --- DEĞİŞİKLİK BURADA: API'den veri çekme ---
  const fetchCountries = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Yeni endpoint'i kullan
      const response = await apiClient.get('/countries-data-light');

      // API'den gelen verinin `response.data` veya `response.data.data` altında
      // bir dizi olduğunu varsayıyoruz. API yanıtınıza göre ayarlayın.
      // Örnek: if (response.data && Array.isArray(response.data.countries))
      if (response.data && Array.isArray(response.data)) { // Direkt dizi dönüyorsa
        const transformedData = transformApiCountryData(response.data);
        setCountries(transformedData);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) { // { data: [...] } şeklinde dönüyorsa
        const transformedData = transformApiCountryData(response.data.data);
        setCountries(transformedData);
      }
      else {
        console.error("API'den /countries-data-light beklenen formatta veri gelmedi:", response.data);
        setCountries([]); // Hata durumunda listeyi boşalt veya önceki state'i koru
        throw new Error("Ülke verileri alınamadı."); // Hata fırlat
      }
    } catch (e) {
      console.error("Ülkeler çekilirken hata:", e);
      // Axios hatasında sunucu metni `error` alanında; kendi fırlattığımız
      // format hatasında yanıt yok, e.message kullanılır.
      setError(e.response ? readApiError(e, "Ülkeler yüklenirken bir hata oluştu.").message : (e.message || "Ülkeler yüklenirken bir hata oluştu."));
      setCountries([]); // Hata durumunda listeyi boşalt
    } finally {
      setIsLoading(false);
    }
  }, []);
  // --- /DEĞİŞİKLİK BURADA ---

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const toggleCountryStatus = async (countryToToggle) => {
    const originalCountry = countries.find(c => c.id === countryToToggle.id);
    if (!originalCountry) return;

    const originalStatus = originalCountry.isActive;
    const newStatus = !originalStatus;

    // Optimistic UI
    setCountries(prevCountries =>
      prevCountries.map(c =>
        c.id === countryToToggle.id ? { ...c, isActive: newStatus } : c
      )
    );
    setSavingStates(prev => ({ ...prev, [originalCountry.id]: true }));

    try {
        const requestBody = {
            country: originalCountry.country, // Path için gereken ana ülke adı
            isActive: newStatus,
            // API'nızın PATCH için beklediği diğer zorunlu alanlar varsa buraya ekleyin
            // flag: originalCountry.flag,
            // countryDE: originalCountry.countryDE,
            // countryTR: originalCountry.countryTR,
        };
        await apiClient.patch(`/countries-data/${encodeURIComponent(originalCountry.country)}`, requestBody);
        // İsteğe bağlı: Başarı mesajı
        // fetchCountries(); // Veya sadece state güncel kalsın
        toast.success("Başarılı bir şekilde kaydedildi!")
    } catch (err) {
        console.error(`Ülke "${originalCountry.country}" durumu güncellenirken hata:`, err);
        const { message } = readApiError(err, 'Durum güncellenemedi.');
        toast.error(`"${originalCountry.country}": ${message}`);
        setError(`"${originalCountry.country}" durumu güncellenemedi: ${message} Değişiklik geri alındı.`);
        // Hata durumunda state'i geri al
        setCountries(prevCountries =>
            prevCountries.map(c =>
              c.id === originalCountry.id ? { ...c, isActive: originalStatus } : c
            )
        );
    } finally {
        setSavingStates(prev => ({ ...prev, [originalCountry.id]: false }));
    }
  };

  const openEditModal = (countryToEdit) => {
    setSelectedCountryForEdit(countryToEdit);
    setModalError('');
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedCountryForEdit(null);
    setModalError('');
  };

  const handleSaveCountryChanges = async (originalCountryName, updatedDataFromForm) => {
    setModalError('');
    const countryToUpdateInState = countries.find(c => c.country === originalCountryName);
    if (!countryToUpdateInState) {
        setModalError("Düzenlenecek ülke bulunamadı (state).");
        return false;
    }

    // Modal için ayrı bir saving state daha iyi olabilir. Şimdilik genel isLoading'i kullanmıyoruz.
    // setIsSaving(true); // Eğer böyle bir state varsa
    let success = false;
    try {
        await apiClient.patch(`/countries-data/${encodeURIComponent(originalCountryName)}`, updatedDataFromForm);

        // Başarılı olursa state'i güncelle
        setCountries(prevCountries =>
          prevCountries.map(c => {
            if (c.country === originalCountryName) {
              // Eğer 'country' (varsayılan ad) değiştiyse ve ID'miz buna bağlıysa ID'yi de güncellemeliyiz.
              // transformApiCountryData'daki ID mantığına göre burayı ayarlayın.
              // Şimdilik ID'nin sabit kaldığını veya API'den gelen bir ID olduğunu varsayıyoruz.
              return { ...c, ...updatedDataFromForm };
            }
            return c;
          })
        );
        toast.success(`"${updatedDataFromForm.country || originalCountryName}" bilgileri güncellendi.`);
        success = true;
    } catch (err) {
        console.error(`Ülke "${originalCountryName}" güncellenirken hata:`, err);
        const apiErrorMessage = readApiError(err, "Ülke güncellenirken bir hata oluştu.").message;
        setModalError(apiErrorMessage);
        success = false;
    } finally {
        // setIsSaving(false);
    }
    return success; // Modal'ın kapanıp kapanmayacağına karar vermek için
  };


  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>
            Ülkeler Yönetimi
            {countries.length > 0 && <span className={`${styles.count} num`}>{countries.length}</span>}
          </h1>
          <p className={styles.subtitle}>
            Ülkelerin çok dilli adlarını ve yayın durumunu yönetin. Anahtarı açtığınızda değişiklik anında kaydedilir.
          </p>
        </div>
      </div>

      {/* Genel sayfa hatası (güncelleme vb. sonrası) */}
      {error && countries.length > 0 && <p className={styles.errorTextPageTop}>{error}</p>}

      {isLoading && countries.length === 0 && (
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <p>Ülkeler yükleniyor...</p>
        </div>
      )}

      {error && countries.length === 0 && !isLoading && (
        <div className={styles.errorPanel}>
          <p className={styles.errorPanelText}>Veriler alınamadı. Bağlantıyı kontrol edip tekrar deneyin.</p>
          <button type="button" onClick={fetchCountries} className={`${styles.retryButton} ghost`}>
            Tekrar dene
          </button>
        </div>
      )}

      {countries.length === 0 && !isLoading && !error && (
        <div className={styles.emptyState}>Gösterilecek ülke bulunamadı.</div>
      )}

      {countries.length > 0 && (
        <div className={styles.tableWrapper}>
        <table className={styles.countriesTable}>
          <thead>
            <tr>
              <th>Bayrak</th>
              {displayLanguageFields.map(langField => (
                <th key={langField.key}>{langField.label}</th>
              ))}
              <th>Durum</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {countries.map(country => (
              <tr key={country.id} className={!country.isActive ? styles.disabledRow : ''}>
                <td>
                  {country.flag ? (
                    <img src={country.flag} alt={`${country.country} Bayrağı`} className={styles.flagImage} onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling && typeof e.target.nextSibling.style !== 'undefined') {
                        e.target.nextSibling.style.display = 'inline';
                      }
                    }}/>
                  ) : (<span style={{ display: country.flag ? 'none' : 'inline' }}>🏴</span>)} {/* Bayrak yoksa veya yüklenemezse emoji */}
                </td>
                {displayLanguageFields.map(langField => (
                  <td key={`${country.id}-${langField.key}`}>
                    {country[langField.key] || '-'}
                  </td>
                ))}
                <td className={styles.statusCell}>
                  <StatusToggle
                    label={`${country.country} — yayın durumu`}
                    checked={country.isActive}
                    saving={savingStates[country.id]}
                    onChange={() => toggleCountryStatus(country)}
                  />
                </td>
                <td className={styles.actionsCell}>
                  <button
                    onClick={() => openEditModal(country)}
                    className={`${styles.actionButton} ${styles.editButton}`}
                  >
                    Düzenle
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {isEditModalOpen && selectedCountryForEdit && (
        <CountryEditModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          countryData={selectedCountryForEdit}
          onSave={handleSaveCountryChanges}
          error={modalError}
        />
      )}
    </div>
  );
};

export default CountriesPage;